// sort.ts — CPU depth ordering for the splat renderer. Gaussians are blended
// back-to-front with premultiplied alpha, so every view change needs a fresh
// order. A 16-bit counting sort over quantized view-space depth is O(n) and
// stable, which keeps a 150k-gaussian cloud well under a frame on a phone.

export const DEPTH_BUCKETS = 65536;

// SortScratch holds reusable buffers so per-frame sorts never allocate.
export class SortScratch {
  depth = new Float32Array(0);
  keys = new Uint16Array(0);
  counts = new Uint32Array(DEPTH_BUCKETS);
  order = new Uint32Array(0);

  ensure(n: number): void {
    if (this.depth.length < n) {
      this.depth = new Float32Array(n);
      this.keys = new Uint16Array(n);
      this.order = new Uint32Array(n);
    }
  }
}

// sortBackToFront orders `visible` (indices into pos) farthest-first for the
// column-major GL view matrix `view` (camera looks down -Z, so a smaller view
// z is farther). Returns a subarray of scratch.order holding the indices.
export function sortBackToFront(
  view: Float32Array,
  pos: Float32Array,
  visible: Uint32Array,
  n: number,
  scratch: SortScratch,
): Uint32Array {
  scratch.ensure(n);
  const { depth, keys, counts, order } = scratch;
  const r0 = view[2], r1 = view[6], r2 = view[10], r3 = view[14];
  let lo = Infinity, hi = -Infinity;
  for (let i = 0; i < n; i++) {
    const g = visible[i] * 3;
    const z = r0 * pos[g] + r1 * pos[g + 1] + r2 * pos[g + 2] + r3;
    depth[i] = z;
    if (z < lo) lo = z;
    if (z > hi) hi = z;
  }
  counts.fill(0);
  const range = hi - lo;
  const scale = range > 0 ? (DEPTH_BUCKETS - 1) / range : 0;
  for (let i = 0; i < n; i++) {
    const k = ((depth[i] - lo) * scale) | 0; // ascending z == far → near
    keys[i] = k;
    counts[k]++;
  }
  let sum = 0;
  for (let b = 0; b < DEPTH_BUCKETS; b++) {
    const c = counts[b];
    counts[b] = sum;
    sum += c;
  }
  for (let i = 0; i < n; i++) order[counts[keys[i]]++] = visible[i];
  return order.subarray(0, n);
}
